import Dropdown from '../Dropdown'
import ResponsiveNavLink, { ResponsiveNavButton } from '../ResponsiveNavLink'
import { DropdownButton } from '../DropdownLink'
import { useAuth } from '../../../hooks/auth'
import React, { useState } from 'react'
import CustomNavLink from '../NavLink';
import {Link, useNavigate} from 'react-router-dom';
import {ReactComponent as Logo} from "../../../assets/img/logo/crafting-ai-logo.svg";
import RoleEnum from "../../enums/RoleEnum";

const Navigation = ({ user }) => {
  // Add custom CSS to hide scrollbars
  React.useEffect(() => {
    // Create a style element
    const style = document.createElement('style');
    // Add the CSS to hide scrollbars across browsers
    style.textContent = `
      .hide-scrollbar {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
    `;
    // Append the style to the document head
    document.head.appendChild(style);
    
    // Clean up function to remove the style when component unmounts
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  let navigate = useNavigate();
  const { logout } = useAuth()
  const [open, setOpen] = useState(false)

  return (
    <nav className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
      {/* Primary Navigation Menu */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between h-16">
          <div className="flex items-center md:w-auto md:min-w-0 md:flex-1">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/">
                <Logo className="dark:fill-white" />
              </Link>
            </div>
            {/* Navigation Links */}
            <div className="hidden md:flex md:w-auto md:min-w-0 md:flex-1 ml-10 overflow-x-auto hide-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="flex space-x-6 whitespace-nowrap pr-6">
              <CustomNavLink
                to="/dashboard/overview"
              >
                Dashboard
              </CustomNavLink>

              <CustomNavLink
                to="/dashboard/skills"
              >
                Skills
              </CustomNavLink>
              <CustomNavLink
                to="/dashboard/prompt-challenges"
              >
                Challenges
              </CustomNavLink>
              <CustomNavLink
                to="/dashboard/prompt-library"
              >
                Library
              </CustomNavLink>

              { user?.role >= RoleEnum.DefaultUser && (
                <CustomNavLink
                  to="/dashboard/teams"
                >
                  Teams
                </CustomNavLink>
              )}

              { user?.role >= RoleEnum.Trainer && (
                  <CustomNavLink
                      to="/dashboard/trainer"
                  >
                    Trainer
                  </CustomNavLink>) }

              { user?.role >= RoleEnum.Admin && (
              <CustomNavLink
                  to="/dashboard/admin"
              >
                Admin
              </CustomNavLink>) }
              </div>
            </div>
          </div>
          {/* Settings Dropdown */}
          <div className="hidden md:flex sm:items-center sm:ml-6">
            {user && !user.isRateLimited && <Dropdown
              align="right"
              width="96"
              trigger={(isOpen) => (
                <button className={`flex items-center text-sm font-sans px-4 py-2 rounded-lg focus:outline-none min-w-[120px] w-auto justify-between
                  ${isOpen ? 'bg-blue-50 border-blue-200 dark:bg-slate-800 dark:border-slate-700' : 'border-transparent'}
                  hover:bg-blue-50 dark:hover:bg-slate-800 hover:border-blue-200 dark:hover:border-slate-700
                  border transition-all duration-200 text-gray-700 dark:text-white`}>
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 dark:bg-slate-700 dark:text-blue-400 mr-2 font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="truncate max-w-[120px]">{user?.name}</div>
                  </div>
                  <div className="ml-1">
                    <svg
                      className="fill-current h-4 w-4 text-gray-500 group-hover:text-blue-600 dark:text-gray-400 dark:group-hover:text-blue-400 transition-colors duration-200"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        // eslint-disable-next-line max-len
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </button>)
              }>
              {/* Decorative gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-blue-600"></div>
              
              <div className="p-5 border-b border-gray-100 dark:border-slate-700">
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 dark:bg-slate-700 dark:text-blue-400 mr-3 font-medium text-base">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 dark:text-white text-base truncate max-w-[200px]">{user?.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{RoleEnum[user?.role]}</div>
                  </div>
                </div>
              </div>
              
              <div className="py-2 px-1">
                <DropdownButton onClick={() => { navigate("/") }} className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span className="whitespace-nowrap">Back to website</span>
                </DropdownButton>
                <DropdownButton onClick={() => { navigate("/dashboard/profile") }} className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="whitespace-nowrap">Profile</span>
                </DropdownButton>
              </div>
              
              <div className="border-t border-gray-100 dark:border-slate-700 py-2 px-1">
                <DropdownButton onClick={logout} className="flex items-center text-red-600 dark:text-red-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="whitespace-nowrap">Logout</span>
                </DropdownButton>
              </div>
            </Dropdown>}
          </div>
          {/* Hamburger */}
          <div className="-mr-2 flex items-center md:hidden">
            <button
              onClick={() => setOpen(open => !open)}
              className="inline-flex items-center justify-center p-2
              rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500
              dark:hover:text-gray-200 hover:bg-gray-100
              dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100
               dark:focus:bg-gray-700 focus:text-gray-500
               dark:focus:text-gray-200 transition duration-150 ease-in-out">
              <svg
                className="h-6 w-6"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24">
                {open ? (
                  <path
                    className="inline-flex"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    className="inline-flex"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      {/* Responsive Navigation Menu */}
      {open && user && !user.isRateLimited && (
        <div className="block md:hidden">
          <div className="pt-2 pb-3 ml-4 mr-4 space-y-1">
            <ResponsiveNavLink
              to="/dashboard/overview"
            >
              Dashboard
            </ResponsiveNavLink>
            <ResponsiveNavLink
                to="/dashboard/skills"
              >
                Skills
              </ResponsiveNavLink>
            <ResponsiveNavLink
                to="/dashboard/prompt-challenges"
              >
                Challenges
              </ResponsiveNavLink>
            <ResponsiveNavLink
                to="/dashboard/prompt-library"
              >
                Library
              </ResponsiveNavLink>

            { user?.role >= RoleEnum.DefaultUser && (
                <ResponsiveNavLink
                  to="/dashboard/teams"
                >
                  Teams
                </ResponsiveNavLink>
            )}

            { user?.role >= RoleEnum.Trainer && (
                <ResponsiveNavLink
                    to="/dashboard/trainer"
                >
                  Trainer
                </ResponsiveNavLink>) }
            { user?.role >= RoleEnum.Admin && (
                <ResponsiveNavLink
                    to="/dashboard/admin"
                >
                  Admin
                </ResponsiveNavLink>) }
          </div>
          {/* Responsive Settings Options */}
          <div className="pb-1 ml-4 mr-4 border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <ResponsiveNavButton onClick={() => { navigate("/dashboard/profile")}}>
                <div className={"flex"}>
                  <div className="flex-shrink-0">
                    <svg
                      className="h-10 w-10 fill-current text-gray-400 dark:text-gray-200"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                      <div className="font-medium text-base text-gray-800 dark:text-gray-200">
                        {user?.name}
                      </div>
                      <div className="font-medium text-sm text-gray-500 dark:text-gray-400">
                        {user?.email}
                      </div>
                  </div>
                    </div>
                  </ResponsiveNavButton>
                </div>
            <div className="mt-3 space-y-1">
              {/* Authentication */}
              <ResponsiveNavButton onClick={logout}>
                Logout
              </ResponsiveNavButton>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navigation
